import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AgenteClientService, TermsOfUseDocumentType } from '@rsm/client/agente-client';
import { MessageService } from '@rsm/shared/message';
import { map, Observable } from 'rxjs';
import { TermsOfUseAcceptDialogComponent } from '../../../../layouts/main/components/terms-of-use-accept-dialog/terms-of-use-accept-dialog.component';
import { CompanySetupService } from '../company-setup.service';

type PlanCode = 'CAMPAIGNS' | 'DEDICATED' | 'POLITICS' | 'ROOT';

interface Plan {
  id: string;
  name: string;
  /** Código estável do plano. Veio junto de `co_plan_code` no mission-api. */
  code: PlanCode;
  /** Falso no plano ROOT, que não vai à vitrine. */
  hireable?: boolean;
  valueTaxaActive: number;
  valuePercenteDiscounte: number;
  details: { description: string }[];
  categories: (Omit<Plan, 'categories'> & { id: string })[];
}

@Component({
  selector: 'app-company-setup-plan',
  templateUrl: './company-setup-plan.component.html',
  styleUrls: ['./company-setup-plan.component.scss'],
})
export class CompanySetupPlanComponent implements OnInit {
  readonly dedicatedMinimumPrice = 1500;
  readonly dedicatedLicenses = 10;
  readonly dedicatedValidity = '1 ano';
  readonly politicsAllowedCnaes = ['325-5', '326-3', '327-1', '328-0', '409-0'];
  readonly politicsCnaeWarning =
    'Atenção: O produto RSM Politics só pode ser contratado por organizações políticas conforme o registro no Cadastro Nacional de Atividades Econômicas - CNAE, como 325-5, 326-3, 327-1, 328-0, 409-0';

  /**
   * Ordem dos três cards do template, que lê `plans[0]`, `plans[1]` e `plans[2]`
   * por posição fixa.
   *
   * <p>Por isso a lista é MONTADA a partir desta ordem, e não apenas ordenada: um
   * plano desconhecido ou não contratável entrando aqui deslocaria os índices e
   * trocaria preço, licenças e termo de todos os cards. Era o que aconteceria com
   * a chegada do plano ROOT — o `sort` anterior era por `indexOf` do nome, e nome
   * de fora da lista devolve -1, indo parar na primeira posição.</p>
   */
  private readonly planOrder: PlanCode[] = ['CAMPAIGNS', 'DEDICATED', 'POLITICS'];

  plans$: Observable<Plan[]> = this.companySetupService.getPlans().pipe(
    map((plans: Plan[]) =>
      this.planOrder
        .map((code) => plans.find((plan) => plan.code === code && plan.hireable !== false))
        .filter((plan): plan is Plan => !!plan)
    )
  );

  isPollitcs = false;

  creditBalance = [0, 0, 0];
  qtdLicenses = 1;
  categorySelected;
  loadingTermForPlanIndex: number | null = null;

  constructor(
    private companySetupService: CompanySetupService,
    private messageService: MessageService,
    private agenteClientService: AgenteClientService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.validIsPollitics();

  }

  hirePlan(plan: Plan, planIndex: number) {
    this.openPlanTerms(plan, planIndex, true);
  }

  viewPlanTerms(plan: Plan, planIndex: number, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.openPlanTerms(plan, planIndex, false);
  }

  private openPlanTerms(plan: Plan, planIndex: number, continueAfterAccept: boolean) {
    const documentType = this.getPlanDocumentType(plan);
    this.loadingTermForPlanIndex = planIndex;
    this.agenteClientService.getActiveTermsOfUse(documentType).subscribe({
      next: (term) => {
        this.loadingTermForPlanIndex = null;
        if (continueAfterAccept && term.accepted === true) {
          this.completeHirePlan(plan, planIndex);
          return;
        }

        this.dialog.open(TermsOfUseAcceptDialogComponent, {
          data: {
            term,
            subtitle: term.accepted === true
              ? 'Consulte os termos aceitos para este produto.'
              : 'Leia o documento até o final para aceitar e continuar para o pagamento.',
            acceptLabel: 'Aceito',
            agreementText: 'Ao contratar, eu concordo com os termos e condições do produto.',
            readonly: !continueAfterAccept && term.accepted === true,
          },
          disableClose: true,
          panelClass: 'terms-of-use-accept-dialog-panel',
        }).afterClosed().subscribe((accepted) => {
          if (accepted && continueAfterAccept) {
            this.completeHirePlan(plan, planIndex);
          }
        });
      },
      error: (error) => {
        this.loadingTermForPlanIndex = null;
        this.messageService.addMsgDanger(error);
      },
    });
  }

  private completeHirePlan(plan: Plan, planIndex: number) {
    // Quem decide é o CÓDIGO do plano; o índice sobrou apenas para indexar
    // `creditBalance`, que é um array paralelo aos três cards do template.
    const isDedicated = plan.code === 'DEDICATED';
    const isPolitics = plan.code === 'POLITICS';
    const qtdLicenses = isDedicated ? this.dedicatedLicenses : this.qtdLicenses;
    const planData = {
      ...plan,
      valueTaxaActive: isPolitics
        ? plan.valueTaxaActive
        : isDedicated
          ? this.getDedicatedPlanPrice(plan)
          : plan.valueTaxaActive,
    };

    this.companySetupService.planInfo = {
      plan: planData,
      creditBalance: isPolitics ? 10000 * qtdLicenses : this.creditBalance[planIndex],
      qtdLicenses : isPolitics ? 1000 * qtdLicenses : qtdLicenses,
      categoryID: plan.id,
      productKey: this.getProductKey(plan),
      rootUserId: this.companySetupService.currentUser?.id,
      rootUserName: this.companySetupService.currentUser?.name,
      validity: isDedicated ? this.dedicatedValidity : undefined,
    };

    this.companySetupService.currentComponentIndex.next(2);
  }

  /** Chave de produto enviada ao mission-api na contratação. */
  private getProductKey(plan: Plan): string {
    switch (plan.code) {
      case 'DEDICATED':
        return 'RSM_DEDICADA';
      case 'POLITICS':
        return 'RSM_POLITICS';
      default:
        return 'RSM_CAMPANHAS';
    }
  }

  private getPlanDocumentType(plan: Plan): TermsOfUseDocumentType {
    switch (plan.code) {
      case 'DEDICATED':
        return 'CLIENT_RSM_DEDICATED';
      case 'POLITICS':
        return 'CLIENT_RSM_POLITICS';
      default:
        return 'CLIENT_RSM_CAMPAIGNS';
    }
  }

  getPlanCategory(plan) {
    return plan.categories.find((c) => c.id === this.categorySelected.id);
  }

  validIsPollitics() {
    const type = this.companySetupService.companyType;

    if (this.hasCompatiblePoliticsCnae(type)) {
      this.isPollitcs = true;
      return;
    }

    const context = this.companySetupService.contextId;
    if (!context) {
      this.isPollitcs = false;
      return;
    }

    this.companySetupService.getCompanyByContextId(context as any).subscribe((res) => {
      this.isPollitcs = this.hasCompatiblePoliticsCnae(
        res?.type.sigla
      );
    });
  }

  private hasCompatiblePoliticsCnae(
    type: string | undefined
  ): boolean {
    if (this.politicsAllowedCnaes.includes(type)) {
      return true;
    }
    return false;
  }

  private normalizeCnae(value: string | undefined): string {
    return String(value || '')
      .split(' - ')[0]
      .trim()
      .toUpperCase();
  }

  getValueByQnt(plan: Plan) {
    if (this.qtdLicenses === 0) {
      return 150;
    }
    const unitPrice = Number(plan?.valueTaxaActive || 0);
    if (plan?.code === 'POLITICS') {
      return unitPrice * this.qtdLicenses * 1000;
    }
    return unitPrice * this.qtdLicenses;
  }

  getDedicatedPlanPrice(plan: Plan): number {
    return Math.max(Number(plan?.valueTaxaActive || 0), this.dedicatedMinimumPrice);
  }

  getDedicatedTotal(plan: Plan): number {
    return this.getDedicatedPlanPrice(plan) + Number(this.creditBalance[1] || 0);
  }

  knowMe(){

    this.messageService.addMsgSuccess({
      message: 'Seu e-mail foi incluído na nossa lista. Assim que tivermos mais informações entraremos em contato!',
      autoclose: true,
    });
  }
}
